import React, { useState, useEffect, ChangeEvent } from "react";
import { Card, CardBody, Container } from "react-bootstrap";
import { useAuth } from "../../../contexts/AuthContext";
import {IQueryTableData, ITableItemChannel } from "../../../component/solution/Interfaces";
import ShowErrorAlert from "../../../component/ShowErrorAlert";
import { Search, X , ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Pencil } from "lucide-react";


export const Channels: React.FC = () => {

     const { fetchData, responseErrorMessage, showErrorAlert }  = useAuth();
     const queryUrlTable: string = `${process.env.REACT_APP_BASE_URL}/${"query/table/data?includeCount=true"}`;

     const defaultQueryState: IQueryTableData = { tableName: "sa_channel", where: "", orderBy: "", pageNum: 0, pageSize: 100};    
     const [queryState, setQueryState] = useState<IQueryTableData>(defaultQueryState);

     const defaultSearchState = { code: "", description: ""};
     const [searchState, setSearchState] = useState<{ [key: string]: string }>(defaultSearchState);

     const [channelItems, setChannelItems] = useState<ITableItemChannel[]>([]);
     const [viewMode, setViewMode] = useState<'default'>('default'); 

    //pagination settings 
    const [pageNumber, setPageNumber] = useState<number>(0); 
    const [pageSize, setPageSize] = useState<number>(100);   
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    //multi-select / single select
    const [selectAllGlobal, setSelectAllGlobal] = useState(false);
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());


     useEffect(() => {
        if(viewMode === "default"){
            fetchChannelItems(); //fetch channels items
        }

     }, [queryState ,viewMode]);

    // pagination updates
    useEffect(() => {
        setQueryState(prev => ({
            ...prev,
            pageNum: pageNumber,
            pageSize: pageSize,
        }));
    }, [pageNumber, pageSize]);

    const fetchChannelItems = () => {
        fetchData(queryUrlTable, {method:"POST", body: JSON.stringify(queryState)}, processData );
    }
    
    
    const processData = (result: any) => {

        const data: ITableItemChannel[] = [];
        
        for (const key in result.data) {
            const item = result.data[key];
            data.push({ ...item.data, ...item.control, id: item.id});
        }

        setChannelItems(data);

        // Pagination 
        setPageSize(pageSize);
        setTotalRecords(result?.numRecords);
        setTotalPages(Math.ceil(result?.numRecords / pageSize));
    }


     const generateWhereClause = (search: { [key: string]: string }) => {
        const clauses = Object.entries(search)
          .filter(([_, value]) => value.trim() !== "")
          .map(([key, value]) => {
            const sanitizedValue = value.replace(/'/g, "''");

            return `${key} ILIKE '%${sanitizedValue}%'`;
          });

      return clauses.join(" AND ");
    };



   const searchClicked = () => {
        // Reset pagination state
        setPageNumber(0); // Reset page to first page
        setPageSize(100); // Reset page size to 100

        // Reset selection state
        setSelectAllGlobal(false);
        setSelectedRowIds(new Set());

        const whereClause = generateWhereClause(searchState);

        setQueryState(prev => ({
          ...prev,
          where: whereClause,
          pageNum: 0, // Ensure that the query state reflects the reset page number
          pageSize: 100, // Ensure the query state reflects the reset page size
        }));
    };

   
   const clearSearchClicked = () => {
       setSearchState(defaultSearchState);
       setQueryState(defaultQueryState);
    }  

    // Pagination handlers
    const goToFirstPage = () => setPageNumber(0);
    const goToPreviousPage = () => setPageNumber(prev => prev - 1);
    const goToNextPage = () => setPageNumber(prev => prev + 1);
    const goToLastPage = () => setPageNumber(totalPages - 1);

    // handle the on search update
    const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { id, value } = event.target;
      setSearchState((prev) => ({ ...prev, [id]: value }));
    };

    // Update page size handler
    const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
    };
                
    // Fields used for rendering the column-specific search inputs in the table header
    const searchFields = [
        { name: 'code', placeholder: 'Search Code...' },
        { name: 'description', placeholder: 'Search Description...'}, 
    ];
        

    // Column headers used to render the table column labels in the main table head
    const columnHeaders = [
        { key: 'channel', label: 'Channel' },
        { key: 'code', label: 'Code' },
        { key: 'description', label: 'Description' },
        { key: 'actions', label: 'Actions' },
    ];

    const currentPageIds = channelItems.map(row => row.id);
    
    const allCurrentPageSelected = selectAllGlobal
      ? currentPageIds.every(id => !selectedRowIds.has(id)) 
      : currentPageIds.every(id => selectedRowIds.has(id));


    const handleRowToggle = (rowId: string) => {
      setSelectedRowIds(prev => {
        const updated = new Set(prev);
        if (updated.has(rowId)) {
          updated.delete(rowId);
        } else {
          updated.add(rowId);
        }
        return updated;
      });
    };

    const handleToggleAll = () => {
      const newSelectAll = !selectAllGlobal;
      setSelectAllGlobal(newSelectAll);
      setSelectedRowIds(new Set()); // reset any manual overrides
    };

    return(
        <React.Fragment>
            { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 
            <Container fluid className="p-0">
                {viewMode === "default" && 
                 <Card>
                   <Card.Header style={{ padding: '15px 0px 0px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <Card.Title>Channels</Card.Title>
                    </div>
                  </Card.Header>
                  <CardBody>
                          <div>
                            <div style={{ width: '100%',  padding: '4px 8px', backgroundColor: 'white' }}>
                                <div className="d-flex justify-content-between">
                                    <div className="d-flex flex-row gap-2" style={{marginTop: '14px'}}>
                                            <h6 className="card-subtitle text-muted">Total Records: {totalRecords} </h6> 
                                            <h6 className="card-subtitle text-muted">Current Page :  [{ pageNumber + 1 } of {totalPages}]</h6> 
                                        </div>

                                        <div className="d-flex align-items-center gap-3">
                                            {/* // Page Size */}
                                            <nav aria-label="">
                                                <ul className="pagination m-0">
                                                    <li className="page-item">
                                                        <select className="page-link" 
                                                            value={pageSize}
                                                            onChange={handlePageSizeChange}
                                                            style={{width: '100px', textAlign: 'center', padding: '10px'}}>
                                                            <option defaultChecked value={pageSize}>{pageSize}</option>
                                                            {[25, 50, 75, 100, 150, 200, 250, 500, 750, 1000].map(size => (
                                                              <option key={size} value={size}>{size}</option>
                                                            ))}
                                                        </select>
                                                    </li>                            
                                                </ul>
                                            </nav>

                                            {/* // Pagination Control */}
                                            <nav aria-label="">
                                              <ul className="pagination m-0">
                                                <li className={`page-item ${pageNumber === 0 ? 'disabled' : ''}`}>
                                                  <button 
                                                    className="page-link d-flex align-items-center justify-content-center gap-1"
                                                    onClick={goToFirstPage}
                                                    disabled={pageNumber === 0}
                                                    style={{ height: '38px', marginTop: '1px' }}
                                                  >
                                                    <ChevronsLeft size={18} />
                                                  </button>
                                                </li>

                                                <li className={`page-item ${pageNumber <= 0 ? 'disabled' : ''}`}>
                                                  <button 
                                                    className="page-link d-flex align-items-center justify-content-center"
                                                    onClick={goToPreviousPage}
                                                    disabled={pageNumber <= 0}
                                                    style={{ height: '38px', marginTop: '1px' }}
                                                  >
                                                    <ChevronLeft size={18} />
                                                  </button>
                                                </li>

                                                <li className="page-item">
                                                  <span
                                                    className="page-link d-flex align-items-center justify-content-center"
                                                    style={{ height: '38px', marginTop: '1px', padding: '0 14px' }}
                                                  >
                                                    Page {pageNumber + 1} of {totalPages}
                                                  </span>
                                                </li>

                                                <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''}`}>
                                                  <button 
                                                    className="page-link d-flex align-items-center justify-content-center"
                                                    onClick={goToNextPage}
                                                    disabled={pageNumber >= totalPages - 1}
                                                    style={{ height: '38px', marginTop: '1px' }}
                                                  >
                                                    <ChevronRight size={18} />
                                                  </button>
                                                </li>

                                                <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''}`}>
                                                  <button 
                                                    className="page-link d-flex align-items-center justify-content-center gap-1"
                                                    onClick={goToLastPage}
                                                    disabled={pageNumber >= totalPages - 1}
                                                    style={{ height: '38px', marginTop: '1px' }}
                                                  >
                                                    <ChevronsRight size={18} />
                                                  </button>
                                                </li>
                                              </ul>
                                            </nav>
                                      </div>
                                </div>
                            </div>
                            <div className="table-responsive">
                            <table className="table table-md table-hover-light w-100 mb-0">
                                {/* FILTER INPUTS HEADER */}
                                <thead>
                                  <tr>
                                    <th style={{ backgroundColor: 'white', margin:'0px', padding: '4px 8px' }}></th> 
                                    <th style={{ backgroundColor: 'white', margin:'0px', padding: '4px 8px' }}></th>
                                     { searchFields.map(({ name, placeholder }) => (
                                         <td key={name} style={{ backgroundColor: 'white', padding: '4px 8px' }}>
                                          <input id={name} name={name} value={searchState[name] ?? ''} onChange={handleSearchOnChange} type="text" autoComplete="off"  placeholder={placeholder} className="form-control form-control-md" />
                                        </td>
                                    ))}
                                    <th style={{ backgroundColor: 'white', padding: '4px 8px', width:'120px' }}>
                                        <div className="d-flex justify-content-end gap-1 w-100">
                                          <button
                                            type="button"
                                            className="btn btn-outline-danger btn-md d-flex justify-content-center align-items-center"
                                            onClick={searchClicked}
                                          >
                                            <Search style={{ width: '20px', height: '20px' }} strokeWidth={2} />
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-md d-flex justify-content-center align-items-center"
                                            onClick={clearSearchClicked}
                                          >
                                            <X style={{ width: '20px', height: '20px' }} strokeWidth={2} />
                                          </button>
                                        </div>
                                      </th>
                                  </tr>
                                </thead>
                                 <thead>
                                <tr>
                                  <td
                                    style={{
                                      fontWeight: "600",
                                      backgroundColor: "white",
                                      border: 'none',
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      height: "40px", // Set a consistent height for the checkbox
                                    }}
                                  >
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={currentPageIds.length > 0 && allCurrentPageSelected}
                                      onChange={handleToggleAll}
                                    />
                                  </td>
                                  {columnHeaders.map(({ key, label }, index) => (
                                    <th
                                      key={key}
                                      style={{
                                        fontWeight: "600",
                                        backgroundColor: "white",
                                        paddingLeft: "20px",
                                        height: "40px", // Make sure all header cells have the same height
                                        ...(index === columnHeaders.length - 1 && { borderRight: "1px solid #deebed" }),
                                      }}
                                    >
                                      {label}
                                    </th>
                                  ))}
                                </tr>
                               </thead>
                                <tbody>
                                   {channelItems.map((record, index) => {

                                    const { id, code, color_code, description} = record;
                                    const cellStyle: React.CSSProperties = { whiteSpace: 'nowrap', padding: '12px 6px', fontSize: '0.75rem', verticalAlign: 'middle', borderRight: 'none', borderLeft: 'none', borderTop:'1px solid #deebed', borderBottom:'1px solid #deebed' };
                                    const isRowChecked = selectAllGlobal ? !selectedRowIds.has(record.id) : selectedRowIds.has(record.id); // explicitly selected

                                    return (
                                           <tr id={`${index}`} key={id}>
                                              <td style={{ ...cellStyle, textAlign: 'center', padding: '0' }}>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '44px', paddingBottom: '4px' }}>
                                                  <input
                                                    type="checkbox"
                                                    checked={isRowChecked}
                                                    className="form-check-input"
                                                    onChange={() => handleRowToggle(id)}
                                                  />
                                                </div>
                                              </td>
                                              <td style={{...cellStyle, paddingLeft: '20px'}}>
                                               <span
                                                title={code}
                                                style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: color_code, border: `2px solid ${color_code}`, display: 'inline-block', verticalAlign: 'middle'}}
                                              />
                                            </td>
                                              <td style={{...cellStyle, paddingLeft: '20px'}} title={code}>
                                                  {code}
                                              </td>
                                              <td style={{ ...cellStyle, paddingLeft: '20px' }} title={description ?? ""}>
                                              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                                                {description ?? ""}
                                              </div>
                                            </td>
                                            <td style={cellStyle} >   
                                                
                                            </td> 
                                          </tr>
                                        );
                                     })} 
                                </tbody>
                              </table>
                              </div>
                            </div>
                  </CardBody>
                </Card>
                }
            </Container>
        </React.Fragment>
    );
}


